<template>
  <div>
    <transition name="component-fade" mode="out-in">
      <div
        key="1"
        v-if="finalFilePath && !uploading"
        class="position-relative"
      >
        <button
          @click="deleteImage"
          class="btn btn-danger btn-sm position-absolute"
          style="top: 10px; right: 10px;"
        >
          {{ 'Delete' | translate }}
        </button>
        <img :src="img(finalFilePath)" class="img-fluid" alt="" style="max-height: 150px;" />
      </div>
      <div key="2" v-else>
        <div
          @dragenter.stop.prevent="handleDrag"
          @dragover.stop.prevent="handleDrag"
          @dragleave.stop.prevent="handleDragEnd"
          @dragend.stop.prevent="handleDragEnd"
          @drop.stop.prevent="handleDrop"
          @click="chooseFile"
          class="d-flex justify-content-center align-items-center border"
          :class="{
            'bg-light': !hover,
            'bg-white': hover,
            'border-primary': hover,
            pointer: !uploading,
          }"
          style="height: 100px;"
        >
          <div v-if="uploading" class="w-75">
            <span>{{ 'Uploading' | translate }}:</span>
            <div class="progress">
              <div
                class="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                :style="`width: ${status}%`"
                :aria-valuenow="status"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          <div class="text-center" v-else>
            <small>
              <b>{{ 'Choose file' | translate }}</b>
              <br>
              {{ 'or drag it here' | translate }}
            </small>
          </div>
        </div>

        <div class="form-check form-switch mt-2" v-if="!disableCompression">
          <input
            type="checkbox"
            class="form-check-input"
            :id="`compress-${id}`"
            v-model="compressUploadedImages"
            value="1"
          />
          <label class="form-check-label" :for="`compress-${id}`">
            <small>{{ 'Compress image' | translate }}</small>
          </label>
        </div>

        <input
          accept="image/*"
          type="file"
          class="d-none"
          ref="fileInput"
          @change="chooseFileSubmit"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import route from './../routes.js'
import _ from 'lodash'
import Compress from 'client-compress'

export default {
  props: ['filePath', 'context', 'disableCompression'],
  data() {
    return {
      id: _.random(0, 100000),
      hover: false,
      uploading: false,
      uploadFinished: false,
      status: 0,
      uploadedFilePath: null,
      files: null,
      compressUploadedImages: this.disableCompression ? false : true,
      compressionOptions: {
        quality: 0.5,
        minQuality: 0.4,
        maxWidth: 3000,
        maxHeight: 2000,
      },
    }
  },
  computed: {
    finalFilePath() {
      return this.uploadedFilePath || this.filePath || null
    }
  },
  methods: {
    chooseFile() {
      this.$refs.fileInput.click()
    },
    chooseFileSubmit() {
      this.submit(this.$refs['fileInput'].files)
    },
    handleDrag(e) {
      if (!this.uploading) {
        this.hover = true
      }
    },
    handleDragEnd(e) {
      if (!this.uploading) {
        this.hover = false
      }
    },
    handleDrop(e) {
      if (!this.uploading) {
        this.hover = false
      }
      if (!this.uploading && e.dataTransfer.files.length) {
        this.hover = false
        this.submit(e.dataTransfer.files)
      }
    },
    updateProgress(progressEvent) {
      this.status = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total,
      )
    },
    validateFileTypes(files) {
      return new Promise((resolve, reject) => {
        let allFilesAreImageType = [...files].every((file) =>
          _.includes(
            ['image/webp', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/gif'],
            _.get(file, 'type'),
          ),
        )

        if (allFilesAreImageType) resolve(files)
        reject(new Error('Unacceptable file type detected'))
      })
    },
    submit(files) {
      this.uploading = true
      this.validateFileTypes(files)
        .then(this.compress)
        .then(this.initiateUpload)
        .then(this.uploadToStorage)
        .then(this.finishUploading)
        .catch(this.handleError)
    },
    compress(files) {
      if (this.compressUploadedImages) {
        let compressor = new Compress(this.compressionOptions)
        return compressor.compress([...files])
      }
      return new Promise((resolve, reject) => {
        resolve(files)
      })
    },
    initiateUpload(files) {
      let isCompressed = _.get(files, '[0].name') ? false : true
      let fileExtension =
        _.get(files, '[0].photo.type') === 'image/jpeg'
          ? 'jpeg'
          : _.get(files, '[0].name')
              .split('.')
              .pop()
      this.files = files
      return axios.post(route.images().initiateUpload, {
        extension: fileExtension,
        filename: ''
      })
    },
    uploadToStorage(response) {
      let isCompressed = _.get(this.files, '[0].name') ? false : true
      let that = this
      let contentType = isCompressed
        ? _.get(this.files, '[0].photo.type')
        : _.get(this.files, '[0].type')
      let config = {
        headers: {
          'Content-Type': contentType,
        },
        transformRequest: [
          (data, headers) => {
            delete headers.common.Authorization
            delete headers.common['X-CSRF-TOKEN']
            return data
          },
        ],
        onUploadProgress: that.updateProgress,
      }
      this.uploadedFilePath = response.data.download
      let data = isCompressed
        ? _.get(this.files, '[0].photo.data')
        : _.get(this.files, '[0]')
      return axios.put(response.data.upload, data, config)
    },
    finishUploading(response) {
      this.uploading = false
      this.$emit('uploadCompleted', this.uploadedFilePath)
    },
    deleteImage() {
      axios
        .post(route.images().delete, {
          path: this.finalFilePath,
          context: this.context,
        })
        .then((response) => {
          if (response.data === true) {
            this.uploadedFilePath = null
            this.$emit('fileDeleted')
          }
        })
        .catch(this.handleError)
    },
    handleError(error) {
      this.uploading = false
      if (error.message === 'Unacceptable file type detected') {
        iziToast.error({
          title: 'Error',
          message: this.$t('Bad file extension'),
          position: 'bottomLeft',
        })
      } else if (error.response && error.response.status === 419) {
        this.$root.$emit(
          'flash',
          this.$t('Your session has expired - please reload'),
          'warning',
        )
      } else {
        console.error(error)
        iziToast.error({
          title: 'Error',
          message: this.$t('Something went wrong'),
          position: 'bottomLeft',
        })
      }
    },
    img(url) {
      if (!url) {
        return false
      }
      return route.image(url)
    },
  },
}
</script>

<style lang="scss">
.pointer {
  cursor: pointer;
}
</style>
